import { PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Row,
  Space
} from 'antd'
import { Rule } from 'antd/es/form'
import { FormInstance } from 'antd/lib'
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import {
  usePersonsServiceGetGuardianshipListEndpoint,
  usePersonsServiceGetGuardianshipListEndpointKey,
  usePersonsServiceGetListPersonUserAssignmentsEndpoint,
  usePersonsServiceGetPersonListByDataEndpoint,
  usePersonsServiceUpdateGuardianshipStatusEndpoint
} from 'src/api/queries'
import {
  ApiError,
  Countries,
  GuardianshipStatus,
  Permissions
} from 'src/api/requests'
import { IDNP_REGEX } from 'src/app-constants'
import {
  CountrySelect,
  GenderSelect,
  requiredRules,
  CardWithTitle,
  CUATMTreeSelect,
  DoctorCard,
  message
} from 'src/components'
import {
  PrisonCard,
  TutorForm,
  TutorSection
} from 'src/features/persons/components/PersonalDetails/components'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot, legalAgePickerValue } from 'src/libs'
import { useLocales } from 'src/locales'
import { clearedErrorsForm, formErrorHandler, toDayjs } from 'src/utils'

type Props = {
  disabled?: boolean
  form?: FormInstance
  isGuardian?: boolean
  isEditing?: boolean
  isViewOnly?: boolean
  isPatient?: boolean
  showAdditionalInfo?: boolean
  showTutor?: boolean
}

export const PersonalDataCard = ({
  disabled = false,
  isGuardian = false,
  isEditing = false,
  showTutor = false,
  form,
  isViewOnly,
  // showAdditionalInfo = true,
  ...props
}: Props) => {
  const { id } = useParams()

  const { t } = useLocales()
  // const { styles } = useStyles()
  const { pathname } = useLocation()
  const { hasPermission } = useUser()

  const { person } = usePersonBreadcrumb(id)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [shouldSearchInMConnect, setShouldSearchInMConnect] = useState(false)

  const onToggle = () => setIsModalOpen((prev) => !prev)

  const queryClient = useQueryClient()

  const isPatient = props.isPatient ?? pathname.includes('patients')

  const isLiveAddressSame = Form.useWatch('isLiveAddressSameAsCurrent', form)
  const isResidenceAddressSame = Form.useWatch(
    'isResidenceAddressSameAsCurrent',
    form
  )

  const residenceCountry = Form.useWatch(['residenceAddress', 'country'], form)
  const isAnonymousChecked = Form.useWatch('isAnonymousChecked', form)
  const isAnonymous = Form.useWatch('isAnonymous', form)
  const citizenship = Form.useWatch('citizenship', form)
  const isMdCitizenship = citizenship === 'md'

  const hasPrison = Form.useWatch('hasPrison', form)

  const idnp = Form.useWatch('idnp', form)

  const { data, isFetching, error, refetch, isFetchedAfterMount } =
    usePersonsServiceGetPersonListByDataEndpoint(
      {
        shouldSearchInMConnect,
        citizenship: Countries.MD,
        idnp
      },
      [],
      { enabled: false }
    )

  useEffect(() => {
    if (!isFetchedAfterMount) return

    if (!error && !isFetching) {
      const dataLength = data?.data?.length ?? 0

      if (!shouldSearchInMConnect && dataLength !== 0) {
        message.error(t('persons.exists_in_system'))
      } else if (!shouldSearchInMConnect && dataLength === 0) {
        setShouldSearchInMConnect(true)
      } else if (shouldSearchInMConnect && dataLength === 0) {
        message.error(t('persons.no_person_found'))
        setShouldSearchInMConnect(false)
      } else if (dataLength > 0) {
        setShouldSearchInMConnect(false)

        const personData = data?.data?.[0]

        form?.setFieldsValue({
          firstName: personData?.firstName,
          lastName: personData?.lastName,
          gender: personData?.gender,
          birthDate: toDayjs(personData?.birthDate)
        })
      }
    }
  }, [data, error, isFetching])

  useEffect(() => {
    if (shouldSearchInMConnect === true) {
      refetch()
    }
  }, [shouldSearchInMConnect])

  const hasTutorship = Form.useWatch('hasTutorship', form)
  const hasDoctors = Form.useWatch('hasDoctors', form)

  const canEditField = () => {
    return !(isAnonymous && isEditing) && disabled
  }

  const getAnonymousUserRules = (requiredRules?: Rule[]) => {
    if (form) {
      const clearedErrors = clearedErrorsForm(form)
      form.setFields(clearedErrors)
    }

    return isAnonymousChecked ? undefined : requiredRules
  }

  const onClick = async () => {
    const values = form?.getFieldsValue()

    if (!values?.idnp) {
      form?.setFields([
        {
          name: 'idnp',
          errors: [t('form.idnp_error')]
        }
      ])
      return
    }

    refetch()
  }
  const isNoneSelected =
    Form.useWatch('tutor', form) === GuardianshipStatus.NO_GUARDIAN_REQUIRED

  const { mutate: changeTutor } =
    usePersonsServiceUpdateGuardianshipStatusEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            usePersonsServiceGetGuardianshipListEndpointKey,
            String(id)
          ]
        })
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const canSeeTutors =
    showTutor &&
    hasPermission(Permissions.PERSONS_GUARDIANSHIP_VIEW_ALL) &&
    hasTutorship

  const { data: tutorList, isLoading: isTutorLoading } =
    usePersonsServiceGetGuardianshipListEndpoint(
      { personId: String(id) },
      [String(id)],
      { enabled: !!id }
    )

  const { data: doctors } =
    usePersonsServiceGetListPersonUserAssignmentsEndpoint(
      {
        personId: String(id)
      },
      [String(id)],
      { enabled: !!id }
    )

  const onChange = (e: RadioChangeEvent) => {
    changeTutor({
      personId: String(id),
      requestBody: {
        status: e.target.value
      }
    })
  }

  useEffect(() => {
    form?.setFieldValue('hasDoctors', (doctors?.data?.length ?? 0) > 0)
  }, [doctors, form])

  return (
    <>
      <CardWithTitle title={t('cases.personal_data')}>
        <Row gutter={[16, 24]}>
          <Form.Item name="isAnonymous" hidden />
          <Col span={24}>
            <Row gutter={[16, 24]}>
              {isAnonymous && (
                <Col span={24}>
                  <Flex gap={8} align="center">
                    <Form.Item
                      name="isAnonymousChecked"
                      valuePropName="checked"
                    >
                      <Checkbox>
                        {t('epidemiology.patient_status.anonymous')} (
                        {form?.getFieldValue('testrapCode')})
                      </Checkbox>
                    </Form.Item>

                    {!isAnonymousChecked && (
                      <Button type="primary" onClick={onClick}>
                        {t('transfer.search_in_mconnect')}
                      </Button>
                    )}
                  </Flex>
                </Col>
              )}
              <Col xs={24} lg={isGuardian ? 12 : 6}>
                <Form.Item
                  name="lastName"
                  label={t('form.last_name')}
                  rules={requiredRules}
                >
                  <Input
                    disabled={canEditField()}
                    placeholder={t('form.last_name')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={isGuardian ? 12 : 6}>
                <Form.Item
                  name="firstName"
                  label={t('form.first_name')}
                  rules={requiredRules}
                >
                  <Input
                    disabled={canEditField()}
                    placeholder={t('form.first_name')}
                  />
                </Form.Item>
              </Col>
              {!isGuardian && isPatient && (
                <>
                  <Col xs={24} lg={6}>
                    <Form.Item
                      name="simeCode"
                      label={t('common.sime_code')}
                      rules={requiredRules}
                    >
                      <Input
                        disabled={disabled}
                        placeholder={t('common.sime_code')}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={6}>
                    <Form.Item name="tarvCode" label={t('common.tarv_code')}>
                      <Input
                        disabled={disabled}
                        placeholder={t('common.tarv_code')}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              <Col xs={24} lg={6}>
                <Form.Item
                  name="gender"
                  label={t('form.gender')}
                  rules={requiredRules}
                >
                  <GenderSelect disabled={canEditField()} />
                </Form.Item>
              </Col>

              <Col xs={24} lg={6}>
                <Form.Item
                  name="birthDate"
                  label={t('form.birth_date')}
                  rules={requiredRules}
                >
                  <DatePicker
                    disabled={canEditField()}
                    className="full-width"
                    placeholder={t('form.birth_date')}
                    defaultPickerValue={legalAgePickerValue}
                    format={dayMonthYearFormatWithDot}
                  />
                </Form.Item>
              </Col>

              {!isAnonymousChecked && (
                <>
                  <Col xs={24} lg={6}>
                    <Form.Item
                      name="citizenship"
                      label={t('common.citizenship')}
                      rules={getAnonymousUserRules(requiredRules)}
                    >
                      <CountrySelect
                        disabled={canEditField()}
                        popupMatchSelectWidth={false}
                        placeholder={t('common.citizenship')}
                      />
                    </Form.Item>
                  </Col>
                  {isMdCitizenship && (
                    <Col xs={24} lg={6}>
                      <Form.Item
                        name="idnp"
                        label={t('form.idnp')}
                        rules={[
                          {
                            pattern: IDNP_REGEX,
                            message: t('form.idnp_error')
                          }
                        ]}
                      >
                        <Input
                          disabled={canEditField() || !isMdCitizenship}
                          placeholder={t('form.idnp')}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  <Col xs={24} lg={6}>
                    <Form.Item
                      name="phone"
                      rules={getAnonymousUserRules(requiredRules)}
                      label={t('common.phone')}
                    >
                      <Input placeholder={t('common.phone')} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={6}>
                    <Form.Item name="email" label={t('form.email_address')}>
                      <Input placeholder={t('form.email_address')} />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
          </Col>

          {!isAnonymousChecked && (
            <>
              <Col xs={24} lg={12}>
                <Row gutter={[16, 24]}>
                  <Col span={24}>
                    <Form.Item
                      name={['currentAddress', 'country']}
                      label={t('form.address')}
                    >
                      <CountrySelect
                        disabled
                        placeholder={t('common.country')}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name={['currentAddress', 'cuatmLocalityId']}
                      label={t('organizational_structure.city')}
                    >
                      <CUATMTreeSelect
                        disabled={isLiveAddressSame || isViewOnly}
                        placeholder={t('organizational_structure.city')}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name={['currentAddress', 'street']}
                      label={t('form.street')}
                    >
                      <Input
                        placeholder="bul. Moscovei"
                        disabled={isLiveAddressSame || isViewOnly}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['currentAddress', 'streetNumber']}
                      label={t('form.building')}
                    >
                      <Input
                        placeholder="5/5"
                        disabled={isLiveAddressSame || isViewOnly}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['currentAddress', 'apartmentNumber']}
                      label={t('form.apartment_number')}
                    >
                      <Input
                        placeholder="ap.90"
                        disabled={isLiveAddressSame || isViewOnly}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} lg={12}>
                <Row gutter={[16, 24]}>
                  <Col span={24}>
                    <Form.Item
                      name={['residenceAddress', 'country']}
                      label={t('cases.residential_address')}
                    >
                      <CountrySelect
                        placeholder={t('common.country')}
                        disabled={isResidenceAddressSame || isViewOnly}
                      />
                    </Form.Item>
                  </Col>

                  {residenceCountry === 'md' && (
                    <Col span={24}>
                      <Form.Item
                        name={['residenceAddress', 'cuatmLocalityId']}
                        label={t('organizational_structure.city')}
                      >
                        <CUATMTreeSelect
                          placeholder={t('organizational_structure.city')}
                          disabled={isResidenceAddressSame || isViewOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  <Col span={24}>
                    <Form.Item
                      name={['residenceAddress', 'street']}
                      label={t('form.street')}
                    >
                      <Input
                        placeholder="bul. Moscovei"
                        disabled={isResidenceAddressSame || isViewOnly}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['residenceAddress', 'streetNumber']}
                      label={t('form.building')}
                    >
                      <Input
                        placeholder="5/5"
                        disabled={isResidenceAddressSame || isViewOnly}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['residenceAddress', 'apartmentNumber']}
                      label={t('form.apartment_number')}
                    >
                      <Input
                        placeholder="ap.90"
                        disabled={isResidenceAddressSame || isViewOnly}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col span={12}>
                <Form.Item
                  valuePropName="checked"
                  name="isResidenceAddressSameAsCurrent"
                >
                  <Checkbox>
                    {t('persons.residence_address_same_current_address')}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  valuePropName="checked"
                  name="isLiveAddressSameAsCurrent"
                >
                  <Checkbox>
                    {t('persons.live_address_same_current_address')}
                  </Checkbox>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </CardWithTitle>

      {!person?.isAnonymous && (
        <Card>
          <Row gutter={[16, 24]}>
            <Col span={6}>
              <Form.Item name="hasPrison" valuePropName="checked">
                <Checkbox>{t('cases.was_in_prison')}</Checkbox>
              </Form.Item>
            </Col>

            {hasPermission(
              Permissions.PERSONS_USER_ASSIGNMENT_VIEW_INSTITUTION
            ) && (
              <Col span={6}>
                <Form.Item name="hasDoctors" valuePropName="checked">
                  <Checkbox>{t('persons.assigned_doctors')}</Checkbox>
                </Form.Item>
              </Col>
            )}

            {canSeeTutors && (
              <Col span={6}>
                <Form.Item name="hasTutorship" valuePropName="checked">
                  <Checkbox>{t('persons.person_tutor')}</Checkbox>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {hasPrison && <PrisonCard person={person} isEditing={isEditing} />}

      {hasPermission(Permissions.PERSONS_USER_ASSIGNMENT_VIEW_INSTITUTION) &&
        hasDoctors && <DoctorCard data={doctors?.data} />}

      {canSeeTutors && (
        <Card
          title={t('persons.person_tutor')}
          {...(!isNoneSelected && {
            actions: [
              <Flex vertical gap={16} key="list">
                {!!tutorList?.guardianships?.length && !isNoneSelected && (
                  <Card bordered={false} loading={isTutorLoading}>
                    <Form.Item name="tutorsList">
                      <TutorForm data={tutorList?.guardianships} />
                    </Form.Item>
                  </Card>
                )}

                {hasPermission(Permissions.PERSONS_GUARDIANSHIP_CREATE_ALL) && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={onToggle}
                    style={{
                      alignSelf: 'center'
                    }}
                  >
                    {t('persons.add_tutor')}
                  </Button>
                )}
              </Flex>
            ]
          })}
        >
          <Form.Item name="tutor" layout="horizontal">
            <Radio.Group onChange={onChange}>
              <Space direction="vertical">
                <Radio value={GuardianshipStatus.NO_GUARDIAN_REQUIRED}>
                  {t('persons.no_tutor')}
                </Radio>
                <Radio value={GuardianshipStatus.IS_GUARDIAN}>
                  {t('persons.parent_tutor')}
                </Radio>
                <Radio value={GuardianshipStatus.UNDER_GUARDIANSHIP}>
                  {t('persons.child_tutor')}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {isModalOpen && <TutorSection onCancel={onToggle} />}
        </Card>
      )}
    </>
  )
}
